import { useCallback, useEffect, useState } from 'react';
import {
  getSchoolConfig,
  updateSchoolConfig
} from '../../../apis/schoolConfig';
import { Feature, SnackbarSeverityType } from '../../../enums';
import {
  ModerationSettings,
  UseModerationConfigArgs,
  UseModerationConfigResult
} from '../types/moderationTypes';
import { SchoolConfig, FeatureConfigs } from '../../../types/schoolConfigType';
import {
  processSchoolConfig,
  toastWarningIfMissingModeration,
  getInvalidEmails,
  buildUpdatedFeatureConfigs
} from '../utils/moderationHelpers';

const useModerationConfig = ({
  school,
  showToast
}: UseModerationConfigArgs): UseModerationConfigResult => {
  const [settings, setSettings] = useState<ModerationSettings>({
    postModerationEnabled: true,
    disabledEmailsCsv: ''
  });
  const [featureConfigsState, setFeatureConfigsState] = useState<
    Partial<Record<Feature, FeatureConfigs>>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    if (!school) return;
    setLoading(true);
    try {
      const response = await getSchoolConfig(school);
      const config = response as unknown as SchoolConfig;
      const {
        featureConfigs,
        moderationFeature,
        settings: derivedSettings
      } = processSchoolConfig(config);
      setFeatureConfigsState(featureConfigs);
      setSettings(derivedSettings);
      toastWarningIfMissingModeration(moderationFeature, showToast);
    } catch (e) {
      showToast?.(
        'Unable to load school configuration. Please refresh.',
        SnackbarSeverityType.error
      );
    } finally {
      setLoading(false);
    }
  }, [school, showToast]);

  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, []);

  const update = useCallback(async (): Promise<boolean> => {
    if (!school) return false;
    setSaving(true);
    try {
      const invalidEmails = getInvalidEmails(settings.disabledEmailsCsv);
      if (invalidEmails.length) {
        showToast?.(
          `Invalid email(s): ${invalidEmails.join(', ')}`,
          SnackbarSeverityType.error
        );
        return false;
      }
      const featureConfigs = buildUpdatedFeatureConfigs(
        featureConfigsState,
        settings
      );
      const payload: Partial<SchoolConfig> = { featureConfigs };
      await updateSchoolConfig(school, payload);
      showToast?.(
        'Moderation settings updated successfully.',
        SnackbarSeverityType.success
      );
      await fetch();
      return true;
    } catch (e: any) {
      showToast?.(
        e?.message || 'Unexpected error while updating settings. Please retry.',
        SnackbarSeverityType.error
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [school, settings, featureConfigsState, fetch, showToast]);

  return { settings, setSettings, loading, saving, update };
};

export default useModerationConfig;
